import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import DocRenderer from './DocRenderer';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import images from '../constants/image.js';
import { Navbar } from "./Navbar.jsx";
import { toast } from "react-toastify";
import Cookies from "js-cookie"
import { ClipLoader } from 'react-spinners';
import { GazeSummary } from './GazeOverlay';
import useGazeKeywords from '../gaze/useGazeKeywords';

const pics = [
  {
    logo: images.PB5,
  },
];

const api = process.env.REACT_APP_API_URL;


function FullScore() {
  const [activeTab, setActiveTab] = useState('resume'); // Manage active tab state
  const location = useLocation();
  const navigate = useNavigate();
  const { data, jobDescriptionFile, jobDescriptionText, resumeFile } = location.state || {};
  const [missingKeywords, setMissingKeywords] = useState([]);
  const [loadingKeywords, setLoadingKeywords] = useState(false);

  // Recruiter Gaze Preview (full keyword list, this page is post-payment)
  const [gazeOn, setGazeOn] = useState(false);
  const [gazeStats, setGazeStats] = useState(null);
  const { keywords: gazeKeywords } = useGazeKeywords(gazeOn);
  const handleGazeStats = useCallback(stats => setGazeStats(stats), []);

  const scoreInt = data && data.score !== undefined ? Math.round(data.score) : 0;
  const sessionId = data ? data.session_id : null;
  const message = scoreInt > 70 ? 'Excellent' : 'Needs Improvement';


  useEffect(() => {

    const fetchData = async () => {
      setLoadingKeywords(true);

      try {
        const token = Cookies.get('access_token');
        if (!token) throw new Error('Access token is missing');


        const keywordsResponse = await fetch(`${api}missing-keywords/?session_id=${encodeURIComponent(sessionId || '')}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Session-Id': sessionId || '',
          },

          credentials: 'include',
        });

        if (!keywordsResponse.ok) {
          // 402/404/410 etc., bail out instead of reading a body that has no
          // missing_keywords array (setting state to undefined -> .map() crash).
          toast.error('Could not load your keywords. Please retry from the upload step.');
          return;
        }

        const keywordsData = await keywordsResponse.json();
        setMissingKeywords(Array.isArray(keywordsData.missing_keywords) ? keywordsData.missing_keywords : []);
      } catch (error) {
      } finally {
        setLoadingKeywords(false);
      }
    };

    fetchData();
  }, [sessionId]);
  // Dependency on sessionId

  return (
    <>
      <Navbar />
      <div className="flex flex-col min-h-screen bg-white md:pl-16 md:pr-16">


        {/* Progress Bar Section */}
        <div className="flex justify-center w-full">
          <img src={pics[0].logo} alt="Progress Bar" className="max-w-full h-auto" />
        </div>

        <div className="flex justify-center text-lg font-medium text-black md:mt-4">
          Resume Score
        </div>

        <div className="flex flex-col md:flex-row bg-transparent min-h-screen min-w-screen md:mt-20">
          {/* Left Section */}
          <div className="flex flex-col flex-grow md:w-1/2">
            <div className="bg-white max-w-2xl w-full mt-16 md:-mt-14 mx-auto rounded-md p-6">
              <div className="flex flex-col items-center">
                <div className="relative w-40 h-40">
                  {/* Full Circle Background */}
                  <div className="relative w-40 h-40">
                    <div className="absolute rounded-full border-4 bg-white border-gray-300"></div>
                    <div
                      className="absolute w-full h-full rounded-full border-4 bg-white border-green-500"
                      style={{
                        clipPath: "circle(50% at 50% 50%)",
                        background: `conic-gradient(
                        #2c5141 ${scoreInt * 3.6}deg,
                        #e3dccd ${(100 - scoreInt) * 3.6}deg
                      )`,
                      }}
                    ></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xl font-bold text-black">{scoreInt}%</span>
                    </div>
                  </div>
                </div>
                <div className="text-center text-lg font-semibold text-black">{message}</div>
                <div className="text-center text-black mt-2">
                  Keep working on your resume to get better results and even better opportunities
                </div>
              </div>
            </div>

            <div className="bg-white max-w-2xl w-full mx-auto mt-4 rounded-md p-6">
              <div className="text-center text-md font-semibold text-gray-600">Missing Keywords</div>
              <div className="text-center text-black mt-2">
                {loadingKeywords ? (
                  <div className="h-[40vh] flex justify-center items-center bg-gray-100 rounded-lg">
                    <div className="text-center">
                      <ClipLoader color="#b08d57" size={50} />
                      <div className="mt-4 text-lg font-medium">Loading Keywords...</div>
                    </div>

                  </div>
                ) : (
                  <div className="flex flex-wrap justify-center items-center gap-4">
                    {missingKeywords.map((keyword, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-2 bg-gray-100 px-4 py-2 rounded-md shadow-sm"
                      >
                        <span className="text-black font-medium">{keyword}</span>
                        <span className="text-green-500">✅</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Right Section */}
          <div className="flex flex-col flex-grow mt-32 md:-mt-14 mx-4 md:mx-6 rounded-md">
            <div className="flex flex-wrap justify-around p-2 bg-white w-full pl-4 pr-4 pt-2 rounded-lg mb-3">
              <button
                className={`flex-grow md:flex-none w-full md:w-36 text-black bg-gray-300 text-center py-2 rounded-lg mb-3 mt-2 transition-colors duration-200 ease-in-out
      ${activeTab === 'resume' ? 'bg-orange-500 text-white' : 'hover:bg-gray-700 active:bg-orange-500'}`}
                onClick={() => setActiveTab('resume')}
              >
                Resume
              </button>

              <button
                className={`flex-grow md:flex-none w-full md:w-48 text-black bg-gray-300 text-center py-2 rounded-lg mb-3 mt-2 transition-colors duration-200 ease-in-out
      ${activeTab === 'jobDescription' ? 'bg-orange-500 text-white' : 'hover:bg-gray-400 active:bg-orange-500'}`}
                onClick={() => setActiveTab('jobDescription')}
              >
                Job Description
              </button>

              {activeTab === 'resume' && resumeFile && (
                <button
                  title="Simulate how a recruiter skims your resume in the first pass"
                  className={`flex-grow md:flex-none w-full md:w-44 text-center py-2 rounded-lg mb-3 mt-2 transition-colors duration-200 ease-in-out
      ${gazeOn ? 'bg-orange-500 text-white' : 'bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100'}`}
                  onClick={() => setGazeOn(v => !v)}
                >
                  👁 Recruiter Gaze
                </button>
              )}
            </div>




            <div className="w-full max-w-screen-md bg-white shadow-md overflow-auto rounded-lg p-5 mx-auto">
              {activeTab === 'resume' ? (
                resumeFile ? (
                  <>
                    <DocRenderer
                      file={resumeFile}
                      gazeEnabled={gazeOn}
                      gazeKeywords={gazeKeywords}
                      onGazeStats={handleGazeStats}
                    />
                    {gazeOn && <GazeSummary stats={gazeStats} />}
                  </>
                ) : <div>No resume uploaded.</div>
              ) : (
                jobDescriptionFile ? (
                  <DocRenderer file={jobDescriptionFile} />
                ) : jobDescriptionText ? (
                  <div>{jobDescriptionText}</div>
                ) : (
                  <div>No job description uploaded.</div>
                )
              )}
            </div>
          </div>
        </div>

        {/* ATS Resume CTA, generation already started in the background at payment time */}
        <div className="mx-4 md:mx-10 my-6 bg-blue-600 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="text-white text-lg font-bold">Your ATS-optimized resume is being prepared</h3>
            <p className="text-blue-100 text-sm mt-1">
              AI is rewriting your resume around every missing keyword, view it and download as PDF or Word.
            </p>
          </div>
          <button
            onClick={() => navigate("/ats-resume")}
            className="flex-shrink-0 px-6 py-3 bg-white text-blue-600 font-semibold rounded-xl hover:bg-blue-50 transition"
          >
            View My ATS Resume →
          </button>
        </div>
      </div>
    </>
  );

}

export default FullScore;
